/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import {
  DataGridPro,
  GridColumnMenu,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
} from '@mui/x-data-grid-pro';
import styles from './styles';
import { renderCell } from './table_config';
import PropTypes from 'prop-types';
import { Paper, styled, ListItemText, MenuItem, Button } from '@mui/material';
import { CustomCsvExport } from '../../../utils/customCSVexport';

const Item = styled(Paper)(({ theme, containerheight }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  height: containerheight ?? '45vh',
}));

const CustomUserItem = () => {
  return (
    <MenuItem>
      <ListItemText>Select Color</ListItemText>
    </MenuItem>
  );
};

const CustomColumnMenu = (props) => {
  return (
    <GridColumnMenu
      {...props}
      slots={{ ColumnMenuUserItem: CustomUserItem }}
      slotProps={{ displayOrder: 15 }}
    />
  );
};

const Datagrid = (props) => {
  const classes = styles();
  const [gridProps, setGridProps] = useState({});
  const cleannedProps = { ...props };
  const columnVisibilityModel = props?.columnVisibilityModel || {};
  const { serverExport } = props;

  const [paginationModel, setPaginationModel] = useState({
    page: props.page ?? 0,
    pageSize: props.pageSize ?? 6,
  });

  useEffect(() => {
    setPaginationModel((prev) => ({
      page: props?.page !== undefined ? props?.page : prev?.page,
      pageSize: props?.pageSize !== undefined ? props?.pageSize : prev?.pageSize,
    }));
  }, [props.page, props.pageSize]);

  delete cleannedProps.pageSize;
  delete cleannedProps.page;
  delete cleannedProps.rowsPerPageOptions;
  delete cleannedProps.onPageSizeChange;
  delete cleannedProps.onPageChange;

  const anyRowDirty = useMemo(() => {
    if (!props?.enableDirtyLock) return false;
    return props?.data?.some((row) => row?.isDirty);
  }, [props?.data, props?.enableDirtyLock]);

  const getRowClassName = (params) => {
    const baseClass = props?.getRowClassName?.(params) || '';

    if (props?.permissions && !props?.permissions?.update && !props?.data?.some((row) => row?.isNewRow)) {
      return `${baseClass} ${classes.rowViewMode}`.trim();
    }

    if (anyRowDirty && !params.row.isDirty) {
      return `${baseClass} ${classes.rowLockedActions}`.trim();
    }

    return baseClass;
  };

  useEffect(() => {
    if (Object.keys(columnVisibilityModel).length > 0) {
      setGridProps((state) => ({
        ...state,
        columnVisibilityModel: { ...columnVisibilityModel },
      }));
    }
  }, [columnVisibilityModel]);
  delete cleannedProps.columnVisibilityModel;

  const [controlledVisibilityModel, setControlledVisibilityModel] = useState(() => {
    const rawColumns = props.columns instanceof Function ? props.columns(renderCell) : props.columns;
    if (!Array.isArray(rawColumns)) return props.columnVisibilityModel || {};

    const initialHiddenFields = {};

    rawColumns.forEach((col) => {
      if (col && Object.prototype.hasOwnProperty.call(col, 'hide') && col.hide === true) {
        initialHiddenFields[col.field] = false;
      }
    });

    return {
      ...initialHiddenFields,
      ...(props.columnVisibilityModel || {})
    };
  });

  const patchedColumns = useMemo(() => {
    const rawColumns = props.columns instanceof Function ? props.columns(renderCell) : props.columns;
    if (!Array.isArray(rawColumns)) return [];

    return rawColumns.map((col) => {
      const cleanCol = { ...col };
      delete cleanCol.hide;
      return cleanCol;
    });
  }, [props.columns]);

  useEffect(() => {
    if (props.columnVisibilityModel && Object.keys(props.columnVisibilityModel).length > 0) {
      setControlledVisibilityModel((prev) => ({
        ...prev,
        ...props.columnVisibilityModel
      }));
    }
  }, [props.columnVisibilityModel]);

  const CustomToolbar = () => {
    const renderToolbarOptions = () => {
      if (cleannedProps.toolbar) {
        const CustomButton = ({ onClick, children }) => (
          // filter undesired props like hideMenu
          <Button onClick={onClick} style={{ padding: '5px' }}>
            {children}
          </Button>
        );
        const CustomExportButton = () => (
          <GridToolbarExportContainer>
            <CustomButton
              onClick={
                () => {
                  if (cleannedProps.toolbar.options.export.serverExportMode) {
                    serverExport();
                  } else {
                    CustomCsvExport(
                      cleannedProps.toolbar.options.export.assetData,
                      [
                        'timestamp',
                        ...cleannedProps.toolbar.options.export.selectedColumns.datakeys
                      ]
                    );
                  }
                }
              }
              style={{ padding: '5px' }}
            >
              Export CSV
            </CustomButton>
          </GridToolbarExportContainer>
        );

        return (
          <GridToolbarContainer>
            {cleannedProps.toolbar.options?.columns && (
              <GridToolbarColumnsButton />
            )}
            {cleannedProps.toolbar.options?.filters && (
              <GridToolbarFilterButton />
            )}
            {cleannedProps.toolbar.options?.density && (
              <GridToolbarDensitySelector />
            )}
            {cleannedProps.toolbar.options.export.enabled &&
              (cleannedProps.toolbar.options.export.options
                ? <CustomExportButton />
                : <GridToolbarExport csvOptions={{
                  fileName: props.fileName || 'Premium Oilfield Technologies'
                }}
                />
              )
            }
          </GridToolbarContainer>
        );
      } else {
        return <GridToolbarContainer></GridToolbarContainer>;
      }
    };

    return renderToolbarOptions();
  };

  useEffect(() => {
    if (!props.apiRef?.current) return;
    const api = props.apiRef.current;

    if (!api._v5Polyfilled) {
      api._v5Polyfilled = true;

      api.setCellMode = (id, field, mode) => {
        const editRowsState = api?.state?.editRows || {};
        const isCurrentlyEditing = !!editRowsState[id]?.[field];
        const rowData = api?.getRow(id);
        if (mode === 'edit' && !isCurrentlyEditing && rowData) {
          api.startCellEditMode({ id, field });
        } else if (mode === 'view' && isCurrentlyEditing && rowData) {
          api.stopCellEditMode({ id, field });
        }
      };

      api.getCellMode = (id, field) => {
        const editRowsState = api?.state?.editRows || {};
        if (field && editRowsState[id]?.[field]) {
          return 'edit';
        }
        return api.getRowMode(id) === 'edit' ? 'edit' : 'view';
      };

      api.commitCellChange = () => {
      };
    }
  }, [props.apiRef, props.data]);

  const handleCellKeyDown = useCallback((params, event) => {
    if (!props.apiRef?.current) return;
    const api = props.apiRef.current;

    if (event.key === 'Enter' || event.key === 'Escape') {
      if (api.getCellMode(params.id, params.field) === 'edit') {
        event.preventDefault();
        event.stopPropagation();

        setTimeout(() => {
          api.commitCellChange({ id: params.id, field: params.field });
          api.setCellMode(params.id, params.field, 'view');
          api.setCellFocus(params.id, params.field);
        }, 0);
      }
    }

    if (event.key === 'Tab') {
      event.preventDefault();

      const allColumns = api.getAllColumns().filter(col => !col.hide);
      const currentFieldIndex = allColumns.findIndex(col => col.field === params.field);
      const direction = event.shiftKey ? -1 : 1;
      const rowId = params.id;

      for (
        let i = currentFieldIndex + direction;
        direction === 1 ? i < allColumns.length : i >= 0;
        i += direction
      ) {
        const nextCol = allColumns[i];

        if (!nextCol || nextCol.field === 'actions') continue;

        const isEditable = nextCol.editable;
        const nextCellParams = api.getCellParams(rowId, nextCol.field);
        const isStringOrNumber =
        typeof nextCellParams.value === 'string' || typeof nextCellParams.value === 'number';

        if (isEditable && isStringOrNumber) {
          setTimeout(() => {
            api.setCellFocus(rowId, nextCol.field);
            api.setCellMode(rowId, nextCol.field, 'edit');
            const cellInput = document?.querySelector(
              `[data-id="${rowId}"] [data-field="${nextCol?.field}"] input`
            );
            if (cellInput) {
              cellInput?.select();
            }
          }, 0);
          break;
        }
      }
    }
  }, [props.apiRef]);

  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
    const legacyDetails = {
      api: {
        ...props.apiRef?.current,
        state: {
          ...(props.apiRef?.current?.state || {}),
          pagination: {
            page: model.page,
            pageSize: model.pageSize,
            pageCount: Math.ceil((props.data?.length || 0) / model.pageSize)
          }
        }
      }
    };
    if (props.onPageSizeChange) {
      props.onPageSizeChange(model.pageSize, legacyDetails);
    }
    if (props.onPageChange) {
      props.onPageChange(model.page, legacyDetails);
    }
    if (props.onPaginationModelChange) {
      props.onPaginationModelChange(model);
    }
  };

  const handleProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      const changedField = Object.keys(newRow).find(
        (field) => newRow[field] !== oldRow[field]
      );

      if (changedField && props?.onCellEditCommit) {
        setTimeout(() => {
          props.onCellEditCommit({
            id: newRow._id ?? newRow.id,
            field: changedField,
            value: newRow[changedField],
          });
        }, 0);
      }

      return newRow;
    },
    [props.onCellEditCommit]
  );

  return (
    <Item elevation={0} containerheight={props.height} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <DataGridPro
        autoHeight
        sx={{ '.MuiDataGrid-columnHeaderTitle': { fontWeight: 'bolder' } }}
        NoRowsOverlay={() => <label>No data to display</label>}
        getRowId={(row) => row._id}
        /**
            NOTE: The position for the {...gridProps} call matter since we required some properties or methods to be overriten
            for some cases, please keep that in mind when need to add, remove or move any property
        */
        {...gridProps}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={props.rowsPerPageOptions ?? [6, 10, 25, 50, 100, 1000]}
        pagination
        className={classes.dataGrid}
        {...cleannedProps}
        rows={props.data}
        columns={patchedColumns}
        columnVisibilityModel={controlledVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => {
          setControlledVisibilityModel(newModel);
          if (props.onColumnVisibilityModelChange) {
            props.onColumnVisibilityModelChange(newModel);
          }
        }}
        processRowUpdate={handleProcessRowUpdate}
        slots={{ columnMenu: CustomColumnMenu, toolbar: CustomToolbar }}
        onCellKeyDown={handleCellKeyDown}
        getRowClassName={getRowClassName}
      />
    </Item>
  );
};

Datagrid.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.oneOfType([
    PropTypes.func.isRequired,
    PropTypes.array.isRequired,
  ]),
  columnVisibilityModel: PropTypes.object,
  pageSize: PropTypes.number,
  height: PropTypes.string,
};

Datagrid.defaultProps = {
  loading: false,
  columnVisibilityModel: {},
  initialState: {},
};

export default Datagrid;
